"use client"

import React, { useEffect, useRef, useState, useId } from "react"
import mermaid from "mermaid"
import { Maximize2, Minimize2 } from "lucide-react"

interface MermaidDiagramProps {
  code: string
  caption?: string
}

export function MermaidDiagram({ code, caption }: MermaidDiagramProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const [svgContent, setSvgContent] = useState<string>("")
  const [error, setError] = useState<boolean>(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const uniqueId = "mermaid-" + useId().replace(/:/g, "")

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "dark",
      themeVariables: {
        fontFamily: "Inter, sans-serif",
        primaryColor: "rgba(34, 211, 238, 0.1)", // premium-cyan
        primaryTextColor: "#e2e8f0",
        primaryBorderColor: "rgba(34, 211, 238, 0.4)",
        lineColor: "#475569",
        secondaryColor: "rgba(139, 92, 246, 0.1)", // premium-violet
        tertiaryColor: "rgba(255, 255, 255, 0.05)",
        surfaceColor: "#050505",
      },
      securityLevel: 'loose'
    })

    const renderChart = async () => {
      try {
        const { svg } = await mermaid.render(uniqueId, code)
        setSvgContent(svg)
        setError(false)
      } catch (err) {
        console.error("Mermaid Render Error:", err)
        setError(true)
      }
    }

    renderChart()
  }, [code, uniqueId])

  return (
    <figure className={`my-12 ${isFullscreen ? 'fixed inset-0 z-50 bg-[#050505]/95 backdrop-blur flex justify-center items-center p-8 m-0' : 'relative'}`}>
      <div 
        className={`w-full overflow-hidden border transition-all duration-300 ${
          isFullscreen 
            ? 'w-full max-w-7xl max-h-[90vh] bg-[#0a0a0a] border-white/10 rounded-2xl p-8 flex justify-center items-center overflow-auto' 
            : 'rounded-xl bg-[#0a0a0a] border-white/5 group'
        }`}
      >
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className={`absolute z-10 p-2 rounded-lg bg-black/50 hover:bg-premium-cyan/20 text-slate-400 hover:text-white transition-all border border-white/5 backdrop-blur ${
            isFullscreen ? 'top-8 right-8' : 'top-4 right-4 opacity-0 group-hover:opacity-100'
          }`}
          aria-label="Toggle Fullscreen"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>

        {error ? (
          <div className="p-8 text-center text-rose-400/80 bg-rose-500/5 text-sm p-4 w-full">
            Invalid Syntax - Diagram Failed to Render
          </div>
        ) : (
          <div className="w-full flex justify-center overflow-x-auto p-4 no-scrollbar">
            <div 
              ref={chartRef} 
              dangerouslySetInnerHTML={{ __html: svgContent }} 
              className={`mermaid-wrapper flex justify-center ${isFullscreen ? 'w-auto max-w-full' : 'w-full'}`}
            />
          </div>
        )}
      </div>
      
      {caption && !isFullscreen && (
        <figcaption className="mt-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
          Fig. <span className="text-white/70">{caption}</span>
        </figcaption>
      )}
    </figure>
  )
}
